import { FC, useEffect, useState } from 'react';
import { DashboardWidgetContainer } from '@app/shared/components/dashboard-widget-container/DashboardWidgetContainer';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import {
  dateFormatter,
  formatMinMaxField,
  getCalculatedProjectValueInPercent,
} from '@app/shared/formatters';
import { enumKeyByValue } from '@app/shared/helpers';
import { OpmSimulatedValue } from '@app/shared/models/contracts/enums/shared-enums';
import { useLocale } from '@app/core/hooks/useLocale';
import styles from './input-summary.module.scss';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
  yearRules,
  textViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { useLocalStateCollapse } from '@app/core/hooks/useLocalStateCollapse';
import {
  setOpmDashboardInputSummaryTableIsEventsExpanded,
  setOpmDashboardInputSummaryTableIsDlocInstrumentsExpanded,
  setOpmDashboardInputSummaryTableIsDlomInstrumentsExpanded,
} from '@app/core/store/ui-values-slice';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import { InputSummaryNetChangeInCapitalSection } from './InputSummaryNetChangeInCapitalSection';
import { InputSummaryDlocSection } from './InputSummaryDlocSection';
import { InputSummaryDlomSection } from './InputSummaryDlomSection';

export const InputSummary: FC = (): JSX.Element => {
  const inputSummary = useAppSelector((state) => state.opmCalculation.riskFreeValues.inputSummary);
  const summaryInstruments = Object.entries(inputSummary.marketValueDlom).map(
    ([instrumentId, instrumentData]) => ({
      instrumentId,
      ...instrumentData,
    })
  );
  const sortedInputSummary = useInstrumentSort(summaryInstruments);
  const opmCalc = useAppSelector((state) => state.opmCalculation);
  const { l } = useLocale();
  const [readyToRender, setReadyToRender] = useState(false);
  const [showDlomSection, setShowDlomSection] = useState<boolean>();
  const [showDlocSection, setShowDlocSection] = useState<boolean>();
  const storedEventsExpandedState = useAppSelector(
    (state) => state.uiValues.userSelections.opmDashboard.inputSummaryTable.isEventsExpanded
  );
  const defaultEventsExpandState = storedEventsExpandedState ?? false;
  const [showNetChangeInCapitalSection, setShowNetChangeInCapitalSection] = useState<boolean>();
  const [showNetChangeInCapitalExpander, setShowNetChangeInCapitalExpander] =
    useState<boolean>(false);
  const [showAllNetChangeInCapital, setShowAllNetChangeInCapital] =
    useState<boolean>(defaultEventsExpandState);

  const storedDlocInstrumentsExpandedState = useAppSelector(
    (state) =>
      state.uiValues.userSelections.opmDashboard.inputSummaryTable.isDlocInstrumentsExpanded
  );
  const storedDlomInstrumentsExpandedState = useAppSelector(
    (state) =>
      state.uiValues.userSelections.opmDashboard.inputSummaryTable.isDlomInstrumentsExpanded
  );
  const project = useAppSelector((state) => state.project.projectDraft);

  // determine if the project is an ERF project
  const buildStructures = useAppSelector((state) => state.capitalStructure.values.buildStructures);
  const capitalStructureId = project.pwermInput.cases[0].capitalStructureId;
  const isErfBuildStructure = buildStructures?.[capitalStructureId].isErf;

  const filteredNetChangeInCapitalEvents: Dictionary<number> = Object.entries(
    inputSummary.netChangeInCapitalEvents || {}
  )
    .filter(([_, value]) => value !== 0)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  const defaultDlocInstrumentsExpandedState = storedDlocInstrumentsExpandedState ?? false;
  const defaultDlomInstrumentsExpandedState = storedDlomInstrumentsExpandedState ?? false;

  const [showAllDlocInstruments, setShowAllDlocInstruments] = useState(
    defaultDlocInstrumentsExpandedState
  );
  const [showAllDlomInstruments, setShowAllDlomInstruments] = useState(
    defaultDlomInstrumentsExpandedState
  );

  useLocalStateCollapse(
    showAllDlocInstruments,
    setOpmDashboardInputSummaryTableIsDlocInstrumentsExpanded
  );
  useLocalStateCollapse(
    showAllDlomInstruments,
    setOpmDashboardInputSummaryTableIsDlomInstrumentsExpanded
  );
  useLocalStateCollapse(
    showAllNetChangeInCapital,
    setOpmDashboardInputSummaryTableIsEventsExpanded
  );

  const currentValuationType =
    opmCalc?.riskFreeValues?.inputs?.opmSimulatedValue ===
    enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.EnterpriseValue)
      ? l('_CurrentEV')
      : l('_CurrentTotalAttributableEquity');

  const currentValuationTypeVolatility =
    opmCalc?.riskFreeValues?.inputs?.opmSimulatedValue ===
    enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.EnterpriseValue)
      ? 'EV'
      : OpmSimulatedValue.Equity;

  const yieldTitle =
    opmCalc?.riskFreeValues?.inputs?.opmSimulatedValue ===
    enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.EnterpriseValue)
      ? l('_FreeCashFlowYield')
      : l('_DividendYield');

  useEffect(() => {
    if (inputSummary) {
      setReadyToRender(true);
      setShowDlomSection(Object.keys(inputSummary.marketValueDlom ?? {}).length > 0);
      setShowDlocSection(Object.keys(inputSummary.dlocByInstrument ?? {}).length > 0);
    }
  }, [inputSummary]);

  useEffect(() => {
    const keys = Object.keys(filteredNetChangeInCapitalEvents);
    setShowNetChangeInCapitalSection(keys.length > 0);
    setShowNetChangeInCapitalExpander(keys.length > 2);
  }, [filteredNetChangeInCapitalEvents]);

  const selectedCase = project.pwermInput.cases.find(
    (c) => c.caseId === inputSummary.selectedCaseId
  );

  return (
    <DashboardWidgetContainer title={l('_OpmInputSummary')} opm className={styles['full-width']}>
      {readyToRender && (
        <table className={styles['summary-table']} data-testid="input-summary-table">
          <tbody>
            {isErfBuildStructure && selectedCase && (
              <tr>
                <td className={styles['title']}>{l('_CaseSelected')}</td>
                <td data-testid="current-ev-or-equity">
                  <CellValue
                    value={selectedCase.narrative}
                    className={`${styles['value']}`}
                    {...textViewRules}
                  />
                </td>
              </tr>
            )}
            <tr>
              <td className={styles['title']}>{l('_ValuationDate')}</td>
              <td
                className={`${styles['value']} table-primary__cell--right`}
                data-testid="valuation-date">
                <CellValue
                  value={dateFormatter(inputSummary.valuationDate)}
                  className={`${styles['value']}`}
                  alignment={Alignment.Right}
                />
              </td>
            </tr>
            <tr>
              <td className={styles['title']}>{currentValuationType}</td>
              <td data-testid="current-ev-or-equity">
                <CellValue
                  value={inputSummary.currentEvOrEquity}
                  className={`${styles['value']}`}
                  {...amountRules}
                />
              </td>
            </tr>
            <InputSummaryNetChangeInCapitalSection
              filteredEvents={filteredNetChangeInCapitalEvents}
              show={showNetChangeInCapitalSection}
              showExpander={showNetChangeInCapitalExpander}
              showAll={showAllNetChangeInCapital}
              setShowAll={setShowAllNetChangeInCapital}
            />
            <tr>
              <td className={styles['title']}>{l('_ForecastExitHorizonWeighted')}</td>
              <td
                className={`${styles['value']} table-primary__cell--right`}
                data-testid="forecast-exit-horizon">
                <CellValue
                  value={inputSummary.forecastExitHorizon}
                  className={`${styles['value']}`}
                  alignment={Alignment.Right}
                  {...yearRules}
                />
              </td>
            </tr>
            <tr>
              <td className={styles['title']}>{l('_RiskFreeRate')}</td>
              <td
                className={`${styles['value']} table-primary__cell--right`}
                data-testid="risk-free-rate">
                <CellValue
                  value={formatMinMaxField(inputSummary.riskFreeRate)}
                  className={`${styles['value']}`}
                  {...(typeof formatMinMaxField(inputSummary.riskFreeRate) === 'number'
                    ? enteredPercentageViewRules
                    : {})}
                  alignment={Alignment.Right}
                />
              </td>
            </tr>
            <tr>
              <td className={styles['title']}>
                {l('_ValuationTypeVolatility', { valuationType: currentValuationTypeVolatility })}
              </td>
              <td
                className={`${styles['value']} table-primary__cell--right`}
                data-testid="ev-or-equity-volatility">
                <CellValue
                  value={formatMinMaxField(inputSummary.evOrEquityVolatility)}
                  className={`${styles['value']}`}
                  {...(typeof formatMinMaxField(inputSummary.evOrEquityVolatility) === 'number'
                    ? enteredPercentageViewRules
                    : {})}
                  alignment={Alignment.Right}
                />
              </td>
            </tr>
            <tr>
              <td className={styles['title']}>{yieldTitle}</td>
              <td
                className={`${styles['value']} table-primary__cell--right`}
                data-testid="free-cashflow-yield">
                <CellValue
                  value={formatMinMaxField(inputSummary.financingCosts)}
                  {...(typeof formatMinMaxField(inputSummary.financingCosts) === 'number'
                    ? enteredPercentageViewRules
                    : {})}
                  className={`${styles['value']}`}
                  alignment={Alignment.Right}
                />
              </td>
            </tr>
            <tr>
              <td className={styles['title']}>{l('_TransactionCostWeighted')}</td>
              <td data-testid="transaction-costs">
                <CellValue
                  className={`${styles['value']}`}
                  value={getCalculatedProjectValueInPercent(inputSummary.transactionCosts)}
                  {...enteredPercentageViewRules}
                />
              </td>
            </tr>
            {inputSummary.ipoDiscount !== null ? (
              <tr>
                <td className={styles['title']}>{l('_IPODiscountWeighted')}</td>
                <td data-testid="ipo-discount">
                  <CellValue
                    className={`${styles['value']}`}
                    value={getCalculatedProjectValueInPercent(inputSummary.ipoDiscount)}
                    {...enteredPercentageViewRules}
                  />
                </td>
              </tr>
            ) : null}
            {inputSummary.postIpoDlom !== null ? (
              <tr>
                <td className={styles['title']}>{l('_PostIPOLockInDiscountWeighted')}</td>
                <td data-testid="post-ipo-dlom">
                  <CellValue
                    className={`${styles['value']}`}
                    value={getCalculatedProjectValueInPercent(inputSummary.postIpoDlom)}
                    {...enteredPercentageViewRules}
                  />
                </td>
              </tr>
            ) : null}
            <InputSummaryDlocSection
              show={showDlocSection}
              sortedInstruments={sortedInputSummary}
              dlocByInstrument={inputSummary.dlocByInstrument}
              dloc={inputSummary.dloc}
              showAll={showAllDlocInstruments}
              setShowAll={setShowAllDlocInstruments}
            />
            <InputSummaryDlomSection
              show={showDlomSection}
              sortedInstruments={sortedInputSummary}
              marketValueDlom={inputSummary.marketValueDlom}
              showAll={showAllDlomInstruments}
              setShowAll={setShowAllDlomInstruments}
            />
            <tr>
              <td className={styles['title']}>{l('_NoOfSimulations')}</td>
              <td data-testid="number-of-simulations">
                <CellValue
                  value={inputSummary.numberOfSimulations}
                  className={`${styles['value']}`}
                  {...amountRules}
                />
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </DashboardWidgetContainer>
  );
};
